import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Autocomplete, Box, TextField, Typography, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { isEmpty, isEqual } from "lodash/lang";
import { isNull, isUndefined, sumBy } from "lodash";
import { withTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";

import ChartScatter from "@plannel/components/rechart/ChartScatter";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import hrchyConfigService from "@plannel/services/data/hrchy-config-service";
import salesTxnService from "@plannel/services/data/sales-txn-service";
import numberUtil from "@plannel/utils/number-util";
import reduxUtil from "@plannel/utils/redux-util";
import axiosUtil from "@plannel/utils/axios-bigint";
import debug from "@plannel/utils/debug-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import PeriodFilter from "@plannel/components/filter/PeriodFilter";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import RatioBarCell from "@plannel/components/aggrid/RatioBarCell";
import Snackbar from "@plannel/components/Snackbar";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import { FS } from "@plannel/utils/font-util";


const CURRENT_DATE = new ZDate();
const START_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() - 11, 1);
const END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth(), 1);
const INIT_DATE = { start: START_DATE, end: END_DATE };
const { snackBarMessageColor } = ColorUtil;
const { SA_AMT, SA_QTY} = CodeUtil.dpMeasure;

const QTY_GRID = "QTY_GRID";
const AMT_GRID = "AMT_GRID";
const ItemCustomerSummary = ({ t, title, viewName }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const userInfo = axiosUtil.jsonbigParse(localStorage.getItem("user"));
  const [chartData, setChartData] = useState([]);
  const hrchyItem = { cd: "item", grpCd: "PHG_1", grpName: "Product Group", name: "Item", seqNum: 4 };
  const hrchyCust = { cd: "customer", grpCd: "SHG_1", grpName: "Sales Org", name: "Customer", seqNum: 4 };
  const [type, setType] = useState(reduxViewState?.type ?? "item");
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState(reduxViewState?.start && reduxViewState?.end ? { start: reduxViewState.start, end: reduxViewState.end } : INIT_DATE);
  const [hrchyLevels, setHrchyLevels] = useState(reduxViewState?.hrchyLevels ?? []);
  const [hrchyLevel, setHrchyLevel] = useState(reduxViewState?.hrchyLevel ?? null);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [amtData, setAmtData] = useState([]);
  const [qtyData, setQtyData] = useState([]);
  const [ratioBarScope, setRatioBarScope] = useState({});
  const curreny = userInfo?.currencySymbol ?? "";
  const reduxState = reduxUtil.createObjectState(viewName);
  reduxState.initialize = true;
  reduxDispatch(reduxUtil.updateViewState({ ...reduxState }));

  const average = useRef({ SA_AMT: 0, SA_QTY: 0 });
  const itemLevelData = useRef([]);
  const custLevelData = useRef([]);
  const qtyGridRef = useRef(null);
  const amtGridRef = useRef(null);
  const [qtyGridLoaded, setQtyGridLoaded] = useState(false);
  const [amtGridLoaded, setAmtGridLoaded] = useState(false);

  const defaultQtyGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, gridId: QTY_GRID });
  
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const defaultAmtGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName, gridId: AMT_GRID });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const numberFormatter = params => {
    if (isNull(params.value) || isUndefined(params.value) || !isFinite(params.value)) {
      return "";
    }
    return numberUtil.formatNumberData(params, { maximumFractionDigits: 2, minimumFractionDigits: 0 });
  }

  const ratioBarColumn = (color, field) => {
    return {
      comparator: (valueA, valueB) => valueA - valueB,
      valueGetter: ({ data }) => data[field],
      // 막대 위에 얹히던 sparkline 툴팁 → 그리드 자체 툴팁(생김새가 앱 전체와 통일된다)
      tooltipValueGetter: ({ value }) => numberFormatter({ value }),
      cellRenderer: RatioBarCell,
      cellRendererParams: {
        color,
        max: (field === SA_AMT.cd ? ratioBarScope.SA_AMT : ratioBarScope.SA_QTY)?.[1] ?? 100,
        formatter: ({ value }) => (field === "SA_AMT" ? curreny : "")
          + numberUtil.formatNumberByAvg(value, average.current[field]),
      }
    }
  };

  const settingName = (data) => {
    if (data) {
      if (data.cd === "item") {
        return "itemName";
      } else if (data.cd === "customer") {
        return "customerName";
      } else {
        return "hrchyName";
      }
    }
    else {
      return "";
    }
  }

  const settingCountName = (data) => {
    if (data) {
      if (data.grpCd === "PHG_1") {
        if(data.cd === "item")
          return "customerCount";
        else{
          return "itemCount";
        }
      }
      else {
        if(data.cd === "customer")
          return "itemCount";
        else{
          return "customerCount";
        }
      }
    }
    else {
      return "";
    }
  }

  const amtColumnDefs = [
    { headerName: settingName(hrchyLevel), field: "Nm", type: ["nonEditableColumn"], flex: 2 },
    {
      headerName: "salesAmt", field: SA_AMT.cd,
      type: ["numberColumn", "nonEditableColumn", "rightAligned"], flex: 3,
      ...ratioBarColumn(ColorUtil.colorSet(0), SA_AMT.cd)
    },
  ];

  const qtyColumnDefs = [
    { headerName: settingName(hrchyLevel), field: "Nm", type: ["nonEditableColumn"], flex: 2 },
    {
      headerName: "salesQty", field: SA_QTY.cd,
      type: ["numberColumn", "nonEditableColumn", "rightAligned"], flex: 3,
      ...ratioBarColumn(ColorUtil.colorSet(1), SA_QTY.cd)
    },
  ];

  const defaultChartConfig = {
    tagField: "Nm", tagName: settingName(hrchyLevel) !== "" ? t(settingName(hrchyLevel)) : "",
    xAxisField: SA_QTY.cd, xAxisName: t("salesQty"),
    yAxisField: SA_AMT.cd, yAxisName: t("salesAmt"),
    zAxisField: "count", zAxisName: settingCountName(hrchyLevel) !== "" ? t(settingCountName(hrchyLevel)) : ""
  }

  const settingHrchyData = (data) => {
    const itemLvData = [];
    const custLvData = [];

    for (const resultData of data) {
      if (resultData.itemHrchyFlg === true) {
        itemLvData.push(resultData);
      }
      else {
        custLvData.push(resultData);
      }
    }

    itemLvData.push(hrchyItem);
    custLvData.push(hrchyCust);
    itemLevelData.current = itemLvData;
    custLevelData.current = custLvData;
    const levels = isEqual(type, "item") ? itemLevelData.current : custLevelData.current;
    setHrchyLevels(levels);
    if (reduxViewState?.hrchyLevel) {
      setHrchyLevel(reduxViewState.hrchyLevel);
    } else {
      setHrchyLevel(levels[levels.length - 1]);
    }
    setLoading(true);
  }

  const getRequestParams = () => {
    const params = {};
    if (period?.start) {
      params.startDate = period.start;
    }

    if (period?.end) {
      params.endDate = period.end;
    }

    if (hrchyLevel) {
      params.hrchyLevelCd = hrchyLevel.cd;
    }

    if (type) {
      params.type = type;
    }
    return params;
  };

  const setDataAndAverage = results => {
    const amtSum = sumBy(results, SA_AMT.cd);
    const qtySum = sumBy(results, SA_QTY.cd);
    const countResult = results.length;
    average.current = { SA_AMT: amtSum / countResult, SA_QTY: qtySum / countResult };
  }

  const settingMeasureData = (data) => {
    const dataArray = [];
    const amtArray = [];
    const qtyArray = [];
    for (const resultData of data) {
      const colDataAmt = {};
      const colDataQty = {};
      if (type === "item") {
        colDataQty.Nm = resultData.itemNm;
        colDataQty.cd = resultData.itemCd;
        colDataQty[SA_QTY.cd] = resultData.salesQty;
        qtyArray.push(colDataQty);

        colDataAmt.Nm = resultData.itemNm;
        colDataAmt.cd = resultData.itemCd;
        colDataAmt.count = resultData.count;
        colDataAmt[SA_AMT.cd] = Math.floor(resultData.salesAmt);
        amtArray.push(colDataAmt);

        colDataAmt[SA_QTY.cd] = resultData.salesQty;
        colDataAmt.type = settingName(hrchyLevel) !== "" ? t(settingName(hrchyLevel)) : "";
        dataArray.push(colDataAmt)
      } else {
        colDataQty.Nm = resultData.customerNm;
        colDataQty.cd = resultData.customerCd;
        colDataQty[SA_QTY.cd] = resultData.salesQty;
        qtyArray.push(colDataQty);

        colDataAmt.Nm = resultData.customerNm;
        colDataAmt.cd = resultData.customerCd;
        colDataAmt[SA_AMT.cd] = Math.floor(resultData.salesAmt);
        colDataAmt.count = resultData.count;
        amtArray.push(colDataAmt);

        colDataAmt[SA_QTY.cd] = resultData.salesQty;
        colDataAmt.type = settingName(hrchyLevel) !== "" ? t(settingName(hrchyLevel)) : "";
        dataArray.push(colDataAmt)
      }
    }
    amtArray.sort((a, b) => (a.SA_AMT < b.SA_AMT) ? 1 : ((b.SA_AMT < a.SA_AMT) ? -1 : 0))
    setAmtData(amtArray);
    qtyArray.sort((a, b) => (a.SA_QTY < b.SA_QTY) ? 1 : ((b.SA_QTY < a.SA_QTY) ? -1 : 0))
    setQtyData(qtyArray);
    const scope = {}
    scope.SA_AMT = amtArray.length > 0 ? [amtArray[amtArray.length - 1].SA_AMT, amtArray[0].SA_AMT] : [0, 100]
    scope.SA_QTY = qtyArray.length > 0 ? [qtyArray[qtyArray.length - 1].SA_QTY, qtyArray[0].SA_QTY] : [0, 100]
    setRatioBarScope(scope)
    setChartData(dataArray);
    if (isEmpty(dataArray)) {
      average.current = { SA_AMT: 0, SA_QTY: 0 };
    } else {
      setDataAndAverage(dataArray);
    }
  };

  const retrieveItemCustomerSummary = () => {
    const params = getRequestParams();
    salesTxnService.getItemCustSum(params)
      .then(response => {
        const { results } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        settingMeasureData(results);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const filterValue = { start: period.start, end: period.end, type, hrchyLevel, hrchyLevels };
        const reduxState = reduxUtil.createObjectState(viewName, filterValue);
        reduxDispatch(reduxUtil.updateViewState(reduxState));
      })
  };

  const retrieveHrchyLevels = () => {
    hrchyConfigService.getAll({ searchFilters: { hrchyNum: CodeUtil.hrchyNum.HRCHY } })
      .then(response => {
        const { results } = response.data;
        settingHrchyData(results);
      })
      .catch(e => {
        debug(e);
      })
  };

  const onRadioChange = e => {
    const radioType = e.target.value;
    setType(radioType);
    if (loading) {
      const levels = isEqual(radioType, "item") ? itemLevelData.current : custLevelData.current;
      setHrchyLevels(levels);
      setHrchyLevel(levels[levels.length - 1]);
    }
  }

  const setGridHighlightCell = (gridApi, Nm, cd) => {

    gridApi.forEachNode(node => {
      if (node?.data?.Nm === Nm && node?.data?.cd === cd) {
        gridApi.ensureIndexVisible(node.rowIndex, 'middle');

        setTimeout(() => { gridApi.flashCells({ rowNodes: [node], flashDelay: 2000, fadeDelay: 1000 }); }, 100);
      }
    });
  }

  const onScatterClick = data => {
    if (data) {
    const { Nm, cd } = data;
    if (Nm && cd) {
      setGridHighlightCell(amtGridRef.current.api, Nm, cd);
      setGridHighlightCell(qtyGridRef.current.api, Nm, cd);
    }
    }
  }

  const onGridReady = (params, gridId) => {
    if (gridId === QTY_GRID) {
      setQtyGridLoaded(true);
      GridUtils.setColumnDefs({...params, columnDefs: qtyColumnDefs, gridId: QTY_GRID, viewName, initState: true});
    } else if (gridId === AMT_GRID) {
      setAmtGridLoaded(true);
      GridUtils.setColumnDefs({...params, columnDefs: amtColumnDefs, gridId: AMT_GRID, viewName, initState: true});
    }
  }

  useEffect(() => {
    if (qtyGridLoaded && amtGridLoaded && !isEmpty(qtyData) && !isEmpty(amtData)) {
      GridUtils.setColumnDefs({ ...qtyGridRef.current, columnDefs: qtyColumnDefs, viewName, gridId: QTY_GRID});
      GridUtils.setColumnDefs({ ...amtGridRef.current, columnDefs: amtColumnDefs, viewName, gridId: AMT_GRID});
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amtData, qtyData, qtyGridLoaded, amtGridLoaded])
  
  useEffect(() => {
    retrieveHrchyLevels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (period?.start && period?.end && hrchyLevel) {
      retrieveItemCustomerSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, hrchyLevel]);

  return (
    <>
      <FilterContainer hasIcons={false}>
        <PeriodFilter period={period} setPeriod={setPeriod} dateType="month" />
        <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
          <RadioGroup row aria-label="levelOption" name="radio-buttons-group" value={type} onChange={onRadioChange}>
            <FormControlLabel value="item" control={<Radio size="small" sx={{ '& .MuiSvgIcon-root': { fontSize: 16 } }} />}
              label={<Typography sx={{ fontWeight: 300, fontSize: FS.H2 }}>{t("item")}</Typography>} />
            <FormControlLabel value="customer" control={<Radio size="small" sx={{ '& .MuiSvgIcon-root': { fontSize: 16 } }} />}
              label={<Typography sx={{ fontWeight: 300, fontSize: FS.H2 }}>{t("customer")}</Typography>} />
          </RadioGroup>
          <Autocomplete
            id="combo-box-demo"
            value={hrchyLevel}
            onChange={(_, newValue) => setHrchyLevel(newValue)}
            getOptionLabel={option => option.name}
            isOptionEqualToValue={(option, value) => isEqual(option.id, value.id)}
            options={hrchyLevels}
            size="small"
            disableClearable
            sx={{ width: 300 }}
            renderInput={(params) => <TextField {...params} label={t("hrchyLv")} />}
          />
        </Box>
      </FilterContainer>
      <Box id={"item-customer-summary"} className={"grid-area"} sx={{ display: "inline-flex" }} width="100%" height="calc(100vh - 140px)">
        <Box id="item-customer-summary-chart" sx={{ width: "calc(50% - 4px)", mr: 1, height: "calc(100% - 20px)" }}>
          <ChartScatter
            scatterConfig={defaultChartConfig}
            data={chartData}
            onScatterClick={onScatterClick}
            isRandomColor
          />
        </Box>
        <Box sx={{ display: "flex", width: "calc(50% - 4px)", height: "calc(100% - 13px)" }}>
          <Box className="ag-theme-balham" sx={{ width: "calc(50% - 5px)", height: "100%", mr: 1, my: 1 }}>
            <AgGridReact
              ref={amtGridRef}
              rowData={amtData}
              {...defaultAmtGridMemo}
              onGridReady={params => onGridReady(params, AMT_GRID)}
            />
          </Box>
          <Box className="ag-theme-balham" sx={{ width: "calc(50% - 5px)", height: "100%", my: 1 }}>
            <AgGridReact
              ref={qtyGridRef}
              rowData={qtyData}
              {...defaultQtyGridMemo}
              onGridReady={params => onGridReady(params, QTY_GRID)}
            />
          </Box>
        </Box>
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ItemCustomerSummary);